import React from "react"
import Layout from "../components/layout"
import SEO from "../components/seo"
import { css } from "@emotion/core"
import { CheckCircle, Mail, Twitter } from "react-feather"

export const CVSection = props => {
  return (
    <section
      css={css`
        width: 50%;
        padding: 20px;
        @media (max-width: 992px) {
          width: 100%;
        }
        @media (max-width: 768px) {
          padding: 0px;
        }
        h3 {
          transition: all 300ms linear;
          -webkit-transition: all 300ms linear;
          &:hover {
            color: var(--color-interactive-light);
          }
        }
        @media print {
          width: 50%;
          padding: 20px;
          padding-bottom: 0px;
          h3 {
            font-size: 1.1em;
          }
        }
      `}
    >
      <h3
        css={css`
          color: var(--color-off-text);
        `}
      >
        {props.title}
      </h3>
      {props.children}
    </section>
  )
}

export const CVBullet = props => {
  return (
    <li
      css={css`
        display: block;
        position: relative;
        padding-left: 30px;
      `}
    >
      <span
        css={css`
          fill: var(--color-interactive-light);
          display: block;
          position: absolute;
          top: 0px;
          left: 0px;
          transform: translateY(2px);
          @media print {
            svg {
              stroke: var(--color-text);
            }
          }
        `}
      >
        <CheckCircle size={18} stroke={"var(--color-interactive-light)"} />
      </span>
      {props.children}
    </li>
  )
}

export const CVSkillTag = props => {
  return (
    <li
      css={css`
        display: inline-block;
        background: var(--color-primary);
        font-weight: bold;
        padding-left: 0.75rem;
        padding-right: 0.75rem;
        margin-right: 1rem;
        border-radius: 0.2rem;
        transition: all 300ms linear;
        -webkit-transition: all 300ms linear;
        &:hover {
          color: var(--color-text-negative);
          background: var(--color-interactive-dark);
        }
        @media print {
          background: white;
          border: 1px solid var(--color-off-text);
          padding-left: 0.5rem;
          padding-right: 0.5rem;
        }
      `}
    >
      {props.children}
    </li>
  )
}

const CVSubtitle = css`
  color: var(--color-off-text);
  margin: 15px 0px;
  display: inline;
`

const CVSkillsTitle = css`
  display: inline;
  padding-right: 1.5rem;
`

export default function CV() {
  return (
    <Layout>
      <SEO title="CV" />
      <h1
        css={css`
          padding-left: 20px;
          @media print {
            display: none;
          }
        `}
      >
        CV
      </h1>
      <div
        css={css`
          display: flex;
          flex-flow: column wrap;
          justify-content: flex-start;
          max-height: 111.550883rem;
          @media (max-width: 992px) {
            flex-flow: column nowrap;
            max-height: 100%;
          }
          @media print {
            max-height: 97.550883rem;
            flex-flow: column wrap;
          }
        `}
      >
        <h2
          css={css`
            position: absolute;
            top: 50px;
            right: 80px;
            display: none;
            @media print {
              display: block;
            }
          `}
        >
          Tim Roderick
        </h2>
        <CVSection title="ABOUT ME">
          <h3>Personal Profile</h3>
          <p>
            Graduate Mathematics and Computer Science student from the
            University of Bristol, working in software at <b>Graphcore</b>.
          </p>
          <h3>Interests</h3>
          <p>
            <b>(1)</b> My preferred topic of study is machine learning, more
            recently understanding the "Transformer"; <b>(2)</b> I enjoy graphic
            design, apply­ing this to my front-end development work; <b>(3)</b>{" "}
            Outside of development, I'm an avid Formula 1 fan and enjoy playing
            guitar.
          </p>
        </CVSection>
        <CVSection title="EXPERIENCE">
          <h3>The Mathworks, Inc.</h3>
          <h4 css={CVSubtitle}>
            Jul 2019 – Sep 2019 | Software Engineer Internship
          </h4>
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
            `}
          >
            <CVBullet>
              Worked in an agile development team that developed an application
              used in MATLAB and Simulink.
            </CVBullet>
            <CVBullet>
              Developed the applications UI in JavaScript and back-end in C++,
              writing tests alongside.
            </CVBullet>
            <CVBullet>
              Educated myself by reading JavaScript Ninja, A Tour of C++, Clean
              Architecture.
            </CVBullet>
            <CVBullet>
              Was the intern coordinator for this summer. This involv­ed
              organising and managing the other interns, and the presentations
              we gave highlighting our work.
            </CVBullet>
          </ul>
          <h3>SBG Sports Software</h3>
          <h4 css={CVSubtitle}>
            Jul 2018 – Aug 2018 | Software Engineer Internship
          </h4>
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
            `}
          >
            <CVBullet>
              Was part of a team that developed software that's used to record
              and tag live sporting events.
            </CVBullet>
            <CVBullet>
              Worked with development tools and frameworks such as the
              Blackmagic Design SDK and Qt.
            </CVBullet>
            <CVBullet>
              Gained a more in-depth understanding of developing multi-threaded
              software in C++.
            </CVBullet>
          </ul>
          <h3>Applied Automation</h3>
          <h4 css={CVSubtitle}>
            Jul 2015 – Aug 2015 | Trainee Software Engineer
          </h4>
          <p>
            <b>(1)</b> Was part of a team that developed software that was used
            to control industrial processes; <b>(2)</b> Developed my skills in
            more outdated or rare programming standards such as ladder logic.
          </p>
        </CVSection>
        <CVSection title="CONTACT">
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
            `}
          >
            <li
              css={css`
                display: block;
                position: relative;
                padding-left: 30px;
              `}
            >
              <span
                css={css`
                  fill: var(--color-interactive-light);
                  display: block;
                  position: absolute;
                  top: 0px;
                  left: 0px;
                  transform: translateY(2px);
                  @media print {
                    svg {
                      stroke: var(--color-text);
                    }
                  }
                `}
              >
                <Twitter size={18} stroke={"var(--color-interactive-light)"} />
              </span>
              @tim_cb_roderick
            </li>
            <li
              css={css`
                display: block;
              `}
            >
              Please email me for further contact details.
            </li>
            <li
              css={css`
                display: block;
              `}
            >
              <i> References available on request</i>
            </li>
          </ul>
        </CVSection>
        <CVSection title="EDUCATION">
          <h3>University of Bristol</h3>
          <h4 css={CVSubtitle}>
            2016 – 2020 | MEng Computer Science and Mathematics
          </h4>
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
            `}
          >
            <CVBullet>
              I achieved a first-class degree, focusing on the study of
              processor architecture and machine lear­ning.
            </CVBullet>
            <CVBullet>
              In machine learning, I achieved first-class grades in all related
              units: "Machine Learning", "Applied Deep Learning", "Monte Carlo
              Methods." My masters thesis was also ML focused and looked at
              utilising the popular "Transformer" architecture for video
              summarisation.
            </CVBullet>
            <CVBullet>
              In processor architecture, I gained a thorough understanding of
              modern processor design by building a CPU simulator from scratch
              as a part of the unit "Advanced Computer Architecture", simulating
              a modern instruction set that utilises Thomasulo's algorithm.
            </CVBullet>
            <CVBullet>
              I enjoyed the more statistical aspects of my Mathematics modules
              such as "Monte Carlo Methods". In my penultimate year mathematics
              project, I designed a stochastic approach for creating optimal car
              park topologies.
            </CVBullet>
          </ul>
          {/* <h3>Secondary School</h3>
          <h4 css={CVSubtitle}>
            2009 – 2016 | A levels: Mathematics (A*), Further Mathematics (A),
            Computing (A).
          </h4> */}
        </CVSection>
        <CVSection title="SKILLS">
          <h3 css={CVSkillsTitle}>Python</h3>
          <h4 css={CVSubtitle}>2015 – Present</h4>
          <p
            css={css`
              margin-top: 1rem;
            `}
          >
            Used primarily for Machine Learning research.
          </p>
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
            `}
          >
            <CVSkillTag>Python</CVSkillTag>
            <CVSkillTag>PyTorch</CVSkillTag>
          </ul>
          <h3 css={CVSkillsTitle}>C++</h3>
          <h4 css={CVSubtitle}>2017 – Present</h4>
          <p
            css={css`
              margin-top: 1rem;
            `}
          >
            Developed test-driven, modern C++.
          </p>
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
            `}
          >
            <CVSkillTag>C++17</CVSkillTag>
            <CVSkillTag>Boost</CVSkillTag>
            <CVSkillTag>OpenCV</CVSkillTag>
            <CVSkillTag>Google Test</CVSkillTag>
          </ul>
          <h3 css={CVSkillsTitle}>Front-End Development</h3>
          <h4 css={CVSubtitle}>2015 – Present</h4>
          <p
            css={css`
              margin-top: 1rem;
            `}
          >
            Developed and designed responsive Desktop and PWA user-interfaces
            for personal and professional use. Grew to use JavaScript more
            effectively as a functional programming language.
          </p>
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
            `}
          >
            <CVSkillTag>ES6</CVSkillTag>
            <CVSkillTag>Functional Programming</CVSkillTag>
            <CVSkillTag>NodeJS</CVSkillTag>
            <CVSkillTag>CSS</CVSkillTag>
            <CVSkillTag>Gatsby</CVSkillTag>
            <CVSkillTag>React</CVSkillTag>
            <CVSkillTag>Dojo</CVSkillTag>
            <CVSkillTag>Affinity Designer</CVSkillTag>
            <CVSkillTag>Adobe Illustrator</CVSkillTag>
            <CVSkillTag>Photoshop</CVSkillTag>
            <CVSkillTag>HTML5</CVSkillTag>
          </ul>
          <h3 css={CVSkillsTitle}>Other Technical Skills</h3>
          <ul
            css={css`
              margin: 0px;
              margin-bottom: 30px;
              margin-top: 1rem;
            `}
          >
            <CVSkillTag>Java</CVSkillTag>
            <CVSkillTag>C#</CVSkillTag>
            <CVSkillTag>GraphQL</CVSkillTag>
            <CVSkillTag>SQL</CVSkillTag>
            <CVSkillTag>R</CVSkillTag>
            <CVSkillTag>LaTeX</CVSkillTag>
            <CVSkillTag>MATLAB</CVSkillTag>
            <CVSkillTag>Windows</CVSkillTag>
            <CVSkillTag>Linux</CVSkillTag>
            <CVSkillTag>Mac</CVSkillTag>
            <CVSkillTag>Git</CVSkillTag>
          </ul>
        </CVSection>
      </div>
    </Layout>
  )
}
